// ════════════════════════════════════════════════════════════
//  online/ledger.js — the Circuit's memory: every finished fight, the
//  leagues of each week, and how far the record is complete
// ════════════════════════════════════════════════════════════
//  games/shatterproof/league/   (2026-10-16: the League restarted as a real league — the old
//                                 Fight Night record under circuit/ is left untouched)
//    weeks/{w}/rows/{fightId}  one ledger row per finished fight (circuit.js
//                              says the shape) — written ONCE, first wins
//    weeks/{w}/league          { circuit, contenders, promoted, relegated, elo, minds }
//                              — minds = every NPC's mind going into the week (mind.js)
//                              — written once, from last week's rows
//    cursor                    the last hour whose every fight is in the rows
//    hall/{season}             a finished season's honours — written once, from its rows
//    hours/{h}/lock            { card, pb, note, kind } — the hour's card and
//                              everyone's playbook, locked at :50 of the hour BEFORE
//                              (10 minutes to look and bet) — or at the hour's start when
//                              it couldn't go early
//
//  ⚑ ORDER IS THE LAW: who fights in hour h depends on every result before
//  it (the table) that its card can KNOW. At the hour's start that's all of
//  them (cursor ≥ h−1). Early, at :50 of the hour before, it's every row of the
//  hours before that one + the last hour's fights that had ended by :50
//  (circuit.js isKnown; cursor ≥ h−2) — the fight still on air then only counts
//  as "fought". A week's leagues are only ever WRITTEN once the cursor has
//  passed the whole of last week. Nothing is decided on a half-known record;
//  a screen that is behind says "catching up" instead.
//
//  ⚠ TESTING-PHASE TRUST (same as bets and results): any signed-in screen can
//  write here. Fine for caP'n and friends; strangers wait on server authority.
// ════════════════════════════════════════════════════════════
import { BASE } from "./ie.js";
import { GENESIS_LEAGUE, GENESIS_MS, hourOf, weekOf, hoursOfWeek, nextLeague, standings, closingRatings, seasonWeeks, seasonAwards, SEASON_WEEKS } from "./circuit.js";
import { genesisMinds, foldMinds } from "./mind.js";
import { membersOf, withMembers } from "./challengers.js";

export const CPATH = `${BASE}/league`;
export const weekPath = (w) => `${CPATH}/weeks/${w}`;
export const hourPath = (h) => `${CPATH}/hours/${h}`;
export const CURSOR = `${CPATH}/cursor`;
export const GENESIS_HOUR = hourOf(GENESIS_MS);
export const GENESIS_WEEK = weekOf(GENESIS_MS);

const ledgers = new WeakMap();
/**
 * The rows, per week, kept in memory and topped up live. One per `net`.
 * rows(w) → Promise<row[]> · add(row) → writes it if nobody has (first wins)
 */
export function ledgerOf(net) {
  let L = ledgers.get(net);
  if (L) return L;
  const weeks = new Map();
  const week = (w) => {
    let W = weeks.get(w);
    if (!W) {
      W = { rows: new Map(), unsaved: new Set() };
      // a failed read is a FAILURE, never "no rows" — a bracket or a league built on nothing would be canon forever
      W.ready = net.get(`${weekPath(w)}/rows`).then((all) => { for (const v of Object.values(all || {})) if (v && v.id) W.rows.set(v.id, v); });
      W.ready.catch(() => { if (weeks.get(w) === W) weeks.delete(w); });   // try again next time
      // then live: every row anyone adds lands here
      if (net.onChild) W.ready.then(() => { W.off = net.onChild(`${weekPath(w)}/rows`, (v) => { if (v && v.id) W.rows.set(v.id, v); }); }, () => {});
      weeks.set(w, W);
    }
    return W;
  };
  L = {
    async rows(w) { const W = week(w); await W.ready; return [...W.rows.values()]; },
    /** The server's rows, read NOW (for anything written once and forever: a lock, a league). */
    async fresh(w) {
      const W = week(w); await W.ready;
      const all = await net.get(`${weekPath(w)}/rows`);
      for (const v of Object.values(all || {})) if (v && v.id) W.rows.set(v.id, v);
      return [...W.rows.values()];
    },
    async has(w, id) { const W = week(w); await W.ready; return W.rows.has(id); },
    async get(w, id) { const W = week(w); await W.ready; return W.rows.get(id) || null; },
    /**
     * Many rows of ONE week in one write (the keeper's paper hours). They land in memory at
     * once, so the next hour can be planned before the write comes back. Only rows nobody
     * has yet are written; a paper row is the same on every screen, so a race rewrites it as-is.
     */
    async addMany(w, rows) {
      const W = week(w);
      await W.ready;
      const upd = {};
      for (const row of rows) {
        if (!row || (W.rows.has(row.id) && !W.unsaved.has(row.id))) continue;
        const { week: _w, ...clean } = row; void _w;
        if (!W.rows.has(row.id)) W.rows.set(row.id, clean);
        upd[row.id] = W.rows.get(row.id);
      }
      if (!Object.keys(upd).length) return null;
      // A WRITE THAT FAILS KEEPS ITS ROWS IN MEMORY, marked unsaved (the next pass sends them again). Found 2026-10-22: it
      // used to DELETE them, and the keeper — already deciding the next hours — then planned those hours on a record with
      // a hole in it (ORDER IS THE LAW broken, forever: a lock is written once). A paper row is the same on every screen,
      // so keeping it is never wrong; it just isn't on the server yet.
      for (const id of Object.keys(upd)) W.unsaved.add(id);
      return Promise.resolve(net.update(`${weekPath(w)}/rows`, upd)).then((v) => { for (const id of Object.keys(upd)) W.unsaved.delete(id); return v; });
    },
    async add(row) {
      const W = week(row.week);
      await W.ready;
      if (W.rows.has(row.id)) return W.rows.get(row.id);
      const { week: _w, ...clean } = row; void _w;
      const r = await net.txn(`${weekPath(row.week)}/rows/${row.id}`, (cur) => (cur ? undefined : clean));
      const v = r.value || clean;
      W.rows.set(row.id, v);
      return v;
    },
  };
  ledgers.set(net, L);
  return L;
}

/** The last hour whose every fight is in the rows. */
export async function cursorOf(net) {
  const c = await net.get(CURSOR).catch(() => null);
  return typeof c === "number" && c >= GENESIS_HOUR - 1 ? c : GENESIS_HOUR - 1;
}
export const advanceCursor = (net, h) => net.txn(CURSOR, (cur) => (typeof cur === "number" && cur >= h ? undefined : h));

const leagues = new WeakMap();
/**
 * A week's leagues. Genesis week: caP'n's two lists. After that: built from last
 * week's leagues + rows — but ONLY once the cursor has passed all of last week
 * (resolves null until then: "catching up").
 */
export async function leagueOf(net, w) {
  let cache = leagues.get(net);
  if (!cache) { cache = new Map(); leagues.set(net, cache); }
  if (cache.has(w)) return cache.get(w);
  let out = null;
  if (w <= GENESIS_WEEK) out = { ...GENESIS_LEAGUE, promoted: [], relegated: [], elo: {}, minds: genesisMinds() };
  else {
    const stored = await net.get(`${weekPath(w)}/league`);
    // a league stored before the Mind existed carries none: its NPCs start the week as themselves
    if (stored && Array.isArray(stored.circuit)) out = { promoted: [], relegated: [], elo: {}, ...stored, minds: foldMinds(stored.minds || genesisMinds(), []) };
    else {
      const prevHours = hoursOfWeek(w - 1);
      if ((await cursorOf(net)) < prevHours[prevHours.length - 1]) return null;   // last week isn't all in yet
      const prev = await leagueOf(net, w - 1);
      if (!prev) return null;
      const rows = await ledgerOf(net).fresh(w - 1);   // the server's own copy: a league is forever
      // CHALLENGERS (2026-10-21): last week's members were on its cards (in the Contenders unless they'd risen) — they go into
      // the stored lists now, so a Challenger who won their Apocalypse series really rises (a failed read: not now)
      const lastPrev = prevHours[prevHours.length - 1];
      const nl = nextLeague(withMembers(prev, await membersOf(net, { strict: true }), lastPrev), rows, w - 1);
      const built = { circuit: nl.circuit, contenders: nl.contenders, promoted: nl.promoted, relegated: nl.relegated, elo: closingRatings(standings(rows, prev.elo), prev.elo), minds: foldMinds(prev.minds, rows) };
      const r = await net.txn(`${weekPath(w)}/league`, (cur) => (cur ? undefined : built));
      const v = r.value || built;
      out = { promoted: [], relegated: [], elo: {}, ...v, minds: foldMinds(v.minds || genesisMinds(), []) };
    }
  }
  cache.set(w, out);
  return out;
}

const halls = new WeakMap();
/**
 * A finished season's honours (circuit.js seasonAwards) — read, or written once by the first
 * screen to find the season over (the cursor past its last hour). null until then.
 */
export async function hallOf(net, s) {
  let cache = halls.get(net);
  if (!cache) { cache = new Map(); halls.set(net, cache); }
  if (cache.has(s)) return cache.get(s);
  const path = `${CPATH}/hall/${s}`;
  let out = await net.get(path);
  if (!out) {
    const weeks = seasonWeeks(s), last = hoursOfWeek(weeks[SEASON_WEEKS - 1]).slice(-1)[0];
    if ((await cursorOf(net)) < last) return null;   // not over yet
    const rows = (await Promise.all(weeks.map((w) => ledgerOf(net).fresh(w)))).flat();   // a hall is forever: the server's rows
    const league = await leagueOf(net, weeks[SEASON_WEEKS - 1]);   // who was in which league as the season ended
    if (!league) return null;
    const built = { season: s, ...seasonAwards(rows, league) };
    const r = await net.txn(path, (cur) => (cur ? undefined : built));
    out = r.value || built;
  }
  cache.set(s, out);
  return out;
}

